import { formatMoney, fromString, MONEY_SCALE, round } from '@aeris/money';
/**
 * O código de erro do gateway, traduzido (F062).
 *
 * A tela mostrava `Motivo: 131047` na cara do atendente — o número cru que a
 * Meta devolve. Quem lê é quem atende, não quem desenvolve: o número não diz
 * o que fazer, e mandar a pessoa procurar na documentação da Meta no meio de
 * um atendimento é o mesmo que não dizer nada.
 *
 * A lista cobre os erros que de fato aparecem no dia a dia de uma loja. Um
 * código desconhecido continua aparecendo CRU, com o número visível — some
 * seria pior, e um "erro desconhecido" sem o código impediria qualquer
 * investigação depois.
 */
const GATEWAY_ERROR_MESSAGE = {
    '131047': 'Passaram 24 horas desde a última mensagem do cliente — só um modelo aprovado sai agora.',
    '131026': 'Este número não recebe no WhatsApp (não tem conta, ou não aceita mensagens da sua empresa).',
    '131051': 'Tipo de mensagem não suportado por este número.',
    '131000': 'Erro interno da Meta. Tente de novo em alguns minutos.',
    '133010': 'O número da loja não está registrado no WhatsApp Business.',
    '130429': 'Muitas mensagens em pouco tempo — a Meta segurou esta. Ela pode ser reenviada.',
    '131031': 'A conta da loja está bloqueada pela Meta. Ninguém consegue enviar até resolver com eles.',
    '132000': 'O modelo foi enviado com um número de variáveis diferente do aprovado.',
    '132001': 'Este modelo não existe (ou não está aprovado) nesta conta.',
    '132015': 'A Meta pausou este modelo por qualidade baixa.',
    '470': 'Passaram 24 horas desde a última mensagem do cliente — só um modelo aprovado sai agora.',
};
export const gatewayErrorMessage = (code) => GATEWAY_ERROR_MESSAGE[code] ?? `Motivo: ${code}`;
/**
 * `{nome}` vira o primeiro nome do cliente. Sem nome (só o número), some — e
 * a vírgula ou o espaço que sobraria antes dele também.
 */
export const fillQuickReply = (text, name) => {
    const first = name?.trim().split(/\s+/)[0] ?? '';
    const pretty = first === ''
        ? ''
        : first.charAt(0).toLocaleUpperCase('pt-BR') +
            first.slice(1).toLocaleLowerCase('pt-BR');
    const filled = pretty === ''
        ? text.replace(/[ \t]*,?[ \t]*\{nome\}/gi, '')
        : text.replace(/\{nome\}/gi, pretty);
    return filled.trim();
};
const MEDIA_LABEL = {
    image: 'Imagem',
    video: 'Vídeo',
    audio: 'Áudio',
    document: 'Documento',
    sticker: 'Figurinha',
};
/** O nome do que chegou, para a prévia da lista e para o card. */
export const mediaLabelOf = (kind) => MEDIA_LABEL[kind] ?? 'Anexo';
/** O texto que o atendimento põe no rascunho (27/09). */
export const money = (wire) => wire === null || wire === undefined || wire.trim() === ''
    ? '—'
    : formatMoney(round(fromString(wire.trim()), MONEY_SCALE, 'half-up'));
export const inStock = (product) => {
    if (!product.tracksStock)
        return null;
    const available = product.stock?.available;
    if (available === undefined)
        return null;
    // Decimal como texto (ADR-0016): positivo é sem sinal e com algum dígito
    // diferente de zero.
    const text = available.trim();
    return !text.startsWith('-') && /[1-9]/.test(text);
};
const positive = (value) => value !== null && !value.trim().startsWith('-') && /[1-9]/.test(value);
/** As cores com nome — o produto de uma cor só não tem nenhuma (F176). */
export const namedColours = (product) => (product.colours ?? []).filter((colour) => colour.name !== null && colour.name.trim() !== '');
/**
 * A linha que vai ao cliente: nome, preço, e se tem pronta entrega.
 *
 * Com cores (F297), diz QUAIS têm: "cores: Preto, Branco (pronta entrega)".
 * A cor de preço diferente leva o preço junto. Sem nenhuma com estoque, é
 * "sem estoque no momento", como antes.
 */
/** O código do produto: o da variação padrão (a primeira, se nenhuma for). */
export const productCode = (product) => {
    const variant = product.variants.find((each) => each.isDefault) ?? product.variants[0];
    const sku = variant?.sku.trim() ?? '';
    return sku === '' ? null : sku;
};
/**
 * O nome em negrito, com o código antes ("*1234 f. MOTO G20*"): entre peças
 * de nome parecido, quem atende marca a certa pelo código.
 */
const titleOf = (product) => {
    const code = productCode(product);
    return `*${code === null ? '' : `${code} `}${product.name}*`;
};
export const productLine = (product) => {
    const colours = namedColours(product);
    const price = product.price?.basePrice ?? null;
    if (colours.length > 0 && product.tracksStock) {
        const ready = colours.filter((colour) => positive(colour.available));
        const priced = (colour) => colour.price !== null && price !== null && colour.price !== price
            ? `${colour.name ?? ''} ${money(colour.price)}`
            : (colour.name ?? '');
        return [
            titleOf(product),
            price === null ? 'preço sob consulta' : money(price),
            ready.length === 0
                ? 'sem estoque no momento'
                : `cores: ${ready.map(priced).join(', ')} (pronta entrega)`,
        ].join(' — ');
    }
    const stock = inStock(product);
    return [
        titleOf(product),
        price === null ? 'preço sob consulta' : money(price),
        stock === null ? null : stock ? 'pronta entrega' : 'sem estoque no momento',
    ]
        .filter((part) => part !== null)
        .join(' — ');
};
/** "4 min", "1 h 20 min": o tempo da primeira resposta, legível no balcão. */
export const responseTimeText = (seconds) => {
    if (seconds === null)
        return '—';
    if (seconds < 60)
        return `${String(seconds)} s`;
    const minutes = Math.round(seconds / 60);
    if (minutes < 60)
        return `${String(minutes)} min`;
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest === 0
        ? `${String(hours)} h`
        : `${String(hours)} h ${String(rest)} min`;
};
//# sourceMappingURL=inbox-text.js.map